"use client";

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { Slot } from "@radix-ui/react-slot";

import { PrimNavigationMenu } from "@/shared-web.ui/prim.navigation-menu";
import { PrimSheet } from "@/shared-web.ui/prim.sheet";
import { AspectRatio } from "@/shared-web.ui/ui.aspect-ratio";
import { Box } from "@/shared-web.ui/ui.box";
import { Flex } from "@/shared-web.ui/ui.flex";
import { Grid } from "@/shared-web.ui/ui.grid";
import { Image } from "@/shared-web.ui/ui.image";
import { Link } from "@/shared-web.ui/ui.link";
import { ScrollAreaFlex } from "@/shared-web.ui/ui.scroll-area";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import { type CategoryCatalogData } from "@/stf.data/schema";

type CategoryData = CategoryCatalogData[];

type CategorySelectorContextProps = {
  getter: {
    getHref: (slug?: string) => string;
    getLinkBtn: () => typeof PrimNavigationMenu.Link | typeof PrimSheet.Close | typeof Slot;
  };
  setter: {
    setCategoryData: (data: CategoryData) => void;
    setMainCategory: (slug: string) => void;
    setSubCategory: (slug: string) => void;
  };
  value: {
    categoryData: CategoryData;
    mainCategory: string;
    subCategory: string;
  };
};

const CategorySelectorContext
  = createContext<CategorySelectorContextProps | null>(null);

const useCategorySelector = () => {
  const context = useContext(CategorySelectorContext);

  if (!context) {
    throw new Error(
      "useCategorySelector must be used within a <CategorySelector.Root />",
    );
  }

  return context;
};

type ModuleType = "NavigationMenu" | "Page" | "Sheet";

interface CategorySelectorRootProps {
  children: ReactNode;
  data: CategoryData;
  moduleType: ModuleType;
}
const CategorySelectorRoot = (props: CategorySelectorRootProps) => {
  const { children, data, moduleType } = props;

  const [mainCategory, setMainCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [categoryData, setCategoryData] = useState<CategoryData>([]);

  const getHref = (slug?: string) => {
    const hrefList = [mainCategory];
    if (subCategory) {
      hrefList.push(subCategory);
    }
    if (slug) {
      hrefList.push(slug);
    }

    return `/search?c=${hrefList.join(".")}`;
  };

  const getLinkBtn = () => {
    if (moduleType === "NavigationMenu") {
      return PrimNavigationMenu.Link;
    }
    else if (moduleType === "Sheet") {
      return PrimSheet.Close;
    }
    else if (moduleType === "Page") {
      return Slot;
    }

    throw new Error("Invalid moduleType");
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setCategoryData(data);
      setMainCategory(data[0].slug);
    }
  }, [data]);

  const value = {
    getter: {
      getHref,
      getLinkBtn,
    },
    setter: {
      setCategoryData,
      setMainCategory,
      setSubCategory,
    },
    value: {
      categoryData,
      mainCategory,
      subCategory,
    },
  };

  return (
    <CategorySelectorContext.Provider
      value={value}
    >
      {children}
    </CategorySelectorContext.Provider>
  );
};

interface MainCategoryContainerProps {
  children: React.ReactNode;
  className?: string;
}
const MainCategoryContainer = (props: MainCategoryContainerProps) => {
  const { children, className } = props;

  return (
    <Flex className={cn(`
      shadow-[inset_0_-1px_0_0_var(--gray-a5)]
    `, className)}
    >
      {children}
    </Flex>
  );
};

interface MainCategoryTabProps {
  label: string;
  slug: string;
}
const MainCategoryTab = (props: MainCategoryTabProps) => {
  const { label, slug } = props;

  const {
    setter: { setMainCategory, setSubCategory },
    value: { categoryData, mainCategory },
  } = useCategorySelector();
  const isActive = mainCategory === slug;

  const onClick = () => {
    setMainCategory(slug);
    const currentCategory = categoryData.find((c) => {
      return c.slug === slug;
    });
    if (currentCategory?.totalDepth === "2") {
      setSubCategory("");
    }

    const firstSubCategory = currentCategory?.child && currentCategory?.child[0].slug;
    if (currentCategory?.totalDepth === "3" && firstSubCategory) {
      setSubCategory(firstSubCategory);
    }
  };

  return (
    <button
      className={cn(
        `
          relative flex items-center justify-center
          rounded-t-3 px-4 py-2
        `,
        isActive
        && `
          before:absolute before:inset-x-0 before:bottom-0
          before:box-border before:h-[3px]
          before:rounded-full before:bg-accent-9
          before:content-['']
        `,
        isActive && `bg-accent-3`,
      )}
      onClick={onClick}
      onMouseEnter={onClick}
      type="button"
    >
      <Text className="rounded-3 px-4 py-1" size="2">
        {label}
      </Text>
    </button>
  );
};

interface SubCategoryItemProps {
  label: string;
  slug: string;
}
const SubCategoryItem = (props: SubCategoryItemProps) => {
  const { label, slug } = props;

  const {
    setter: { setSubCategory },
    value: { subCategory },
  } = useCategorySelector();

  const isActive = subCategory === slug;

  const onClick = () => {
    setSubCategory(slug);
  };

  return (
    <Flex
      align="center"
      className={cn(
        "relative size-20 rounded-l-2 p-1",
        isActive
        && `
          before:absolute before:inset-y-0 before:right-0
          before:box-border before:w-[3px]
          before:rounded-full before:bg-accent-9
          before:content-['']
        `,
        isActive && `bg-accent-3`,
      )}
      direction="column"
      justify="center"
      onClick={onClick}
      onMouseEnter={onClick}
    >
      <Text className="line-clamp-2 text-center" size="1">
        {label}
      </Text>
    </Flex>
  );
};

const SubCategoryColumn = () => {
  const {
    value: { categoryData, mainCategory },
  } = useCategorySelector();
  const currentCategory = categoryData.find((c) => {
    return c.slug === mainCategory;
  });

  if (currentCategory?.totalDepth === "2") {
    return null;
  }

  return (
    <Flex
      className={`
        relative w-[80px] shrink-0 border-r border-gray-a5
      `}
      direction="column"
    >
      <ScrollAreaFlex displayScrollBar={false} orientation="vertical">
        {currentCategory?.child && currentCategory?.child.map((c) => {
          return <SubCategoryItem key={c.slug} label={c.label} slug={c.slug} />;
        })}
      </ScrollAreaFlex>
    </Flex>
  );
};

interface MainCategoryLinkProps {
  label: string;
  slug: string;
  src?: null | string;
}
const MainCategoryLink = (props: MainCategoryLinkProps) => {
  const { label, src } = props;
  const {
    getter: { getHref, getLinkBtn },
  } = useCategorySelector();

  const LinkBtn = getLinkBtn();

  return (
    <LinkBtn
      asChild
      className={`
        relative col-span-3
        hover:scale-105
      `}
    >
      <Link href={getHref()}>
        <AspectRatio ratio={4}>
          {src
            ? (
                <Image
                  alt={label}
                  transformation={[
                    {
                      width: 300,
                      height: 200,
                    },
                  ]}
                  responsive={false}
                  className="rounded-3 object-cover shadow-3"
                  src={src}
                />
              )
            : (
                <Box
                  className={`
                    rounded-3 shadow-3 size-full bg-gray-2
                  `}
                />
              )}

        </AspectRatio>
        <Text
          className={`
            absolute bottom-0 left-0 w-full rounded-b-3
            rounded-t-none bg-panel px-2 text-center
          `}
          size="2"
        >
          {label}
        </Text>
      </Link>
    </LinkBtn>
  );
};

interface CategoryLinkProps {
  label: string;
  slug: string;
  src?: null | string;
}
const CategoryLink = (props: CategoryLinkProps) => {
  const { label, slug, src } = props;
  const {
    getter: { getHref, getLinkBtn },
  } = useCategorySelector();

  const LinkBtn = getLinkBtn();

  return (
    <LinkBtn
      asChild
      className={`
        flex grow-0 flex-col items-start justify-start
        rounded-2 bg-panel shadow-3
        hover:scale-105 hover:shadow-4
      `}
    >
      <Link href={getHref(slug)}>
        <AspectRatio ratio={1}>
          {src
            ? (
                <Image
                  alt={label}
                  transformation={[
                    {
                      width: 200,
                      height: 200,
                    },
                  ]}
                  responsive={false}
                  className="rounded-2 shadow-2 size-full"
                  src={src}
                />
              )
            : (
                <Box
                  className={`
                    rounded-2 shadow-2 size-full bg-gray-2
                  `}
                />
              )}

        </AspectRatio>
        <Flex className="h-11 p-1">
          <Text className="line-clamp-2 pt-1 leading-0.75" size="1">
            {label}
          </Text>
        </Flex>
      </Link>
    </LinkBtn>
  );
};

const CategoryLinkGrid = () => {
  const {
    value: { categoryData, mainCategory, subCategory },
  } = useCategorySelector();

  const currentCategory = categoryData.find((c) => {
    return c.slug === mainCategory;
  });
  const currentSubCategory = currentCategory?.child && currentCategory?.child.find((c) => {
    return c.slug === subCategory;
  });

  const totalDepth = currentCategory?.totalDepth;

  const linkList
    = totalDepth === "2" ? currentCategory?.child : currentSubCategory?.child;

  return (
    <div className="relative size-full">
      <ScrollAreaFlex displayScrollBar={false} orientation="vertical">
        <Flex direction="column" flexGrow="1">
          <Grid columns={totalDepth === "2" ? "4" : "3"} gap="3" m="3">
            {currentSubCategory && totalDepth === "3" && (
              <MainCategoryLink
                label={currentSubCategory?.label}
                slug={currentSubCategory?.slug}
                src={currentSubCategory?.src}
              />
            )}
            {linkList?.map((c) => {
              return (
                <CategoryLink
                  key={c.slug}
                  label={c.label}
                  slug={c.slug}
                  src={c.src}
                />
              );
            })}
          </Grid>
        </Flex>
      </ScrollAreaFlex>
    </div>
  );
};

export {
  type CategoryData,
  CategoryLink,
  CategoryLinkGrid,
  CategorySelectorRoot,
  MainCategoryContainer,
  MainCategoryLink,
  MainCategoryTab,
  SubCategoryColumn,
  SubCategoryItem,
};
